/**
 * Workforce Intelligence Profile Page
 * 
 * Displays the user's personalized WII dashboard including:
 * - WII Score and Level
 * - Seven Forces breakdown
 * - Strengths and Gaps
 * - Recommended Opportunities
 * - Tool Stack Recommendations
 * - Development Pathway
 */

import { useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Brain, Gauge, Target, Shield, Zap, Users, CheckCircle, AlertCircle,
  ArrowRight, Briefcase, TrendingUp, Lightbulb, Cpu, Boxes, Route,
  ChevronRight, Award, BarChart3, Sparkles, Clock, RefreshCw, Download,
  LogIn, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkforceIntelligence } from "@/contexts/WorkforceIntelligenceContext";
import { SEVEN_FORCES_ARRAY, WII_LEVELS } from "@/data/workforceIntelligence";
import BackNavigation from "@/components/BackNavigation";
import { 
  OPPORTUNITY_DATABASE, 
  TOOL_STACKS, 
  getToolById,
  getStackTools 
} from "@/data/aiRegistry";

export default function WorkforceIntelligenceProfile() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const {
    profile,
    isLoading,
    isSaving,
    error,
    hasCompletedAssessment,
    wiiScore,
    wiiLevel,
    readinessLevel,
    forceScores,
    strengthDetails,
    gapDetails,
    certificationLevel,
    scoreOpportunity,
    scoreTool,
    getDevelopmentPathway,
    generateCertificate,
    getCertificates,
    resetProfile
  } = useWorkforceIntelligence();

  const [activeDemo, setActiveDemo] = useState(null);
  const [isGeneratingCert, setIsGeneratingCert] = useState(false);
  const [certificates, setCertificates] = useState([]);

  // Handle certificate generation
  const handleGenerateCertificate = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error("Please log in to generate a certificate");
      navigate("/auth/login");
      return;
    }
    
    setIsGeneratingCert(true);
    try {
      const cert = await generateCertificate();
      toast.success(`Certificate generated! Number: ${cert.certificate_number}`);
      // Refresh certificates list
      const certs = await getCertificates();
      setCertificates(certs);
    } catch (err) {
      toast.error(err.message || "Failed to generate certificate");
    } finally {
      setIsGeneratingCert(false);
    }
  }, [isAuthenticated, generateCertificate, getCertificates, navigate]);

  // Get personalized opportunities
  const personalizedOpportunities = useMemo(() => {
    if (!hasCompletedAssessment) return [];
    
    const allOpportunities = [
      ...OPPORTUNITY_DATABASE.business.map(o => ({ ...o, category: 'business' })),
      ...OPPORTUNITY_DATABASE.service.map(o => ({ ...o, category: 'service' })),
      ...OPPORTUNITY_DATABASE.career.map(o => ({ ...o, category: 'career' }))
    ];
    
    return allOpportunities
      .map(opp => ({
        ...opp,
        ...scoreOpportunity(opp)
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);
  }, [hasCompletedAssessment, scoreOpportunity]);

  // Get personalized tool stacks
  const personalizedStacks = useMemo(() => {
    if (!hasCompletedAssessment) return [];
    
    return TOOL_STACKS
      .filter(stack => {
        // Filter based on readiness level
        if (readinessLevel === 'basic') {
          return stack.aiReadinessRequired === 'basic';
        }
        if (readinessLevel === 'developing') {
          return ['basic', 'intermediate'].includes(stack.aiReadinessRequired);
        }
        return true;
      })
      .slice(0, 3);
  }, [hasCompletedAssessment, readinessLevel]);

  // Get development pathway
  const developmentPathway = useMemo(() => {
    if (!hasCompletedAssessment) return [];
    return getDevelopmentPathway();
  }, [hasCompletedAssessment, getDevelopmentPathway]);

  // Handle demo profile load
  const handleLoadDemo = (type) => {
    loadDemoProfile(type);
    setActiveDemo(type);
  };

  // Get WII level color
  const getWiiColor = () => {
    if (!wiiLevel) return '#9CA3AF';
    return WII_LEVELS[wiiLevel.id.toUpperCase()]?.color || '#9CA3AF';
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'business': return Briefcase;
      case 'service': return Target;
      case 'career': return TrendingUp;
      default: return Lightbulb;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="wii-profile-page">
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation />
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="flex items-center gap-2 mb-6">
            <Link to="/juswork" className="text-sm text-white/60 hover:text-white">
              JusWork
            </Link>
            <span className="text-white/40">/</span>
            <span className="text-sm text-white font-medium">Intelligence Profile</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Brain className="w-8 h-8" />
              <h1 className="font-heading text-3xl sm:text-4xl font-bold">
                Workforce Intelligence Profile
              </h1>
            </div>
            <p className="text-lg text-white/80 max-w-2xl">
              Your personalized workforce intelligence assessment, showing your WII score, 
              strengths, development areas, and matched opportunities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Assessment Required / Login Prompt */}
      {!hasCompletedAssessment && (
        <section className="bg-white border-b border-[#E5E7EB] py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border-[#E5E7EB] bg-gradient-to-r from-[#991B1B]/5 to-[#1E3A8A]/5">
              <CardContent className="p-8">
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  <div className="flex-1 text-center lg:text-left">
                    <div className="flex items-center gap-3 justify-center lg:justify-start mb-4">
                      <AlertCircle className="w-8 h-8 text-[#991B1B]" />
                      <h2 className="font-heading text-2xl font-bold text-[#1E3A8A]">
                        Complete Your Assessment
                      </h2>
                    </div>
                    <p className="text-[#1E3A8A]/70 mb-6 max-w-lg">
                      Take the Workforce Intelligence Assessment to generate your personalized profile, 
                      discover your strengths, identify development areas, and get matched opportunities.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                      {!isAuthenticated && (
                        <Link to="/auth/login">
                          <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A]">
                            <LogIn className="w-4 h-4 mr-2" />
                            Log In First
                          </Button>
                        </Link>
                      )}
                      <Link to="/observatories/global/survey">
                        <Button className="bg-[#991B1B] text-white hover:bg-[#991B1B]/90">
                          <Sparkles className="w-4 h-4 mr-2" />
                          Start Assessment
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-32 h-32 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center">
                      <Brain className="w-16 h-16 text-[#1E3A8A]/40" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {hasCompletedAssessment ? (
          <div className="space-y-12">
            {/* WII Score Card */}
            <section>
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Main WII Score */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="lg:col-span-1"
                >
                  <Card className="border-2 h-full" style={{ borderColor: getWiiColor() }} data-testid="wii-score-card">
                    <CardContent className="p-8 text-center">
                      <div className="mb-4">
                        <Gauge className="w-12 h-12 mx-auto" style={{ color: getWiiColor() }} />
                      </div>
                      <p className="text-sm font-medium text-[#1E3A8A]/60 mb-2">
                        Workforce Intelligence Index
                      </p>
                      <div 
                        className="text-6xl font-bold mb-2"
                        style={{ color: getWiiColor() }}
                        data-testid="wii-score-value"
                      >
                        {wiiScore}
                      </div>
                      <Badge 
                        className="text-white text-sm px-4 py-1"
                        style={{ backgroundColor: getWiiColor() }}
                      >
                        {wiiLevel?.label || 'Unknown'}
                      </Badge>
                      <p className="text-sm text-[#1E3A8A]/70 mt-4">
                        {wiiLevel?.description}
                      </p>
                      
                      {/* Certification Level */}
                      {certificationLevel && (
                        <div className="mt-6 pt-4 border-t border-[#E5E7EB]">
                          <div className="flex items-center justify-center gap-2 mb-3">
                            <Award className="w-5 h-5 text-[#991B1B]" />
                            <span className="text-sm font-semibold text-[#1E3A8A]">
                              {certificationLevel} Level
                            </span>
                          </div>
                          <Button
                            size="sm"
                            onClick={handleGenerateCertificate}
                            disabled={isGeneratingCert || !isAuthenticated}
                            className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90"
                          >
                            {isGeneratingCert ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-2" />
                                Get Certificate
                              </>
                            )}
                          </Button>
                          {!isAuthenticated && (
                            <p className="text-xs text-[#1E3A8A]/50 mt-2">
                              Log in to generate certificate
                            </p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Strengths & Gaps */}
                <div className="lg:col-span-2 grid md:grid-cols-2 gap-6">
                  {/* Strengths */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    <Card className="h-full border-[#E5E7EB]" data-testid="strengths-card">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <h3 className="font-heading text-lg font-bold text-green-700">Your Strengths</h3>
                        </div>
                        <div className="space-y-3">
                          {strengthDetails.slice(0, 3).map((force, i) => (
                            <div key={force.id} className="flex items-center gap-3">
                              <div 
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: force.color }}
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium text-[#1E3A8A]">{force.shortName}</span>
                                  <span className="text-sm font-bold" style={{ color: force.color }}>{force.score}</span>
                                </div>
                                <Progress value={force.score} className="h-1.5" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Gaps */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <Card className="h-full border-[#E5E7EB]" data-testid="gaps-card">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <AlertCircle className="w-5 h-5 text-orange-600" />
                          <h3 className="font-heading text-lg font-bold text-orange-700">Development Areas</h3>
                        </div>
                        <div className="space-y-3">
                          {gapDetails.slice(0, 3).map((force, i) => (
                            <div key={force.id} className="flex items-center gap-3">
                              <div 
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: force.color }}
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium text-[#1E3A8A]">{force.shortName}</span>
                                  <span className="text-sm font-bold" style={{ color: force.color }}>{force.score}</span>
                                </div>
                                <Progress value={force.score} className="h-1.5" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>
              </div>
            </section>

            {/* Seven Forces Breakdown */}
            <section data-testid="seven-forces-breakdown">
              <h2 className="font-heading text-2xl font-bold mb-6" style={{ color: '#991B1B' }}>
                Seven Forces Breakdown
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {SEVEN_FORCES_ARRAY.map((force, index) => {
                  const score = forceScores?.[force.id] || 0;
                  return (
                    <motion.div
                      key={force.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                    >
                      <Card className="border-[#E5E7EB] hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span 
                              className="text-xs font-bold px-2 py-0.5 rounded"
                              style={{ backgroundColor: `${force.color}20`, color: force.color }}
                            >
                              {force.shortName}
                            </span>
                            <span className="text-lg font-bold" style={{ color: force.color }}>
                              {score}
                            </span>
                          </div>
                          <Progress 
                            value={score} 
                            className="h-2 mb-2"
                          />
                          <p className="text-xs text-[#1E3A8A]/60 line-clamp-2">
                            {force.description.split('.')[0]}.
                          </p>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </section>

            {/* Recommended Opportunities */}
            <section data-testid="recommended-opportunities">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl font-bold" style={{ color: '#991B1B' }}>
                  Recommended Opportunities
                </h2>
                <Link to="/zodiac-lab/workforce/opportunities">
                  <Button variant="outline" className="border-[#991B1B] text-[#991B1B]">
                    View All
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {personalizedOpportunities.map((opp, index) => {
                  const Icon = getCategoryIcon(opp.category);
                  return (
                    <motion.div
                      key={opp.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                    >
                      <Card className="h-full border-[#E5E7EB] hover:shadow-lg transition-shadow">
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Icon className="w-5 h-5 text-[#1E3A8A]" />
                              <span className="text-xs font-medium text-[#1E3A8A]/60 capitalize">{opp.category}</span>
                            </div>
                            <Badge 
                              className={`text-xs ${
                                opp.score >= 75 ? 'bg-green-100 text-green-700' : 
                                opp.score >= 50 ? 'bg-blue-100 text-blue-700' : 
                                'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {opp.score}% Match
                            </Badge>
                          </div>
                          <h4 className="font-heading font-bold text-[#991B1B] mb-2">{opp.name}</h4>
                          <p className="text-sm text-[#1E3A8A]/70 mb-3 line-clamp-2">{opp.description}</p>
                          <div className="text-xs text-[#1E3A8A]/50">
                            {Object.values(opp.matchDetails || {}).filter(Boolean).slice(0, 2).join(' • ')}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </section>

            {/* Recommended Tool Stacks */}
            <section data-testid="recommended-stacks">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl font-bold" style={{ color: '#991B1B' }}>
                  Recommended Tool Stacks
                </h2>
                <Link to="/observatories/ai/registry">
                  <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A]">
                    View Registry
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {personalizedStacks.map((stack, index) => (
                  <motion.div
                    key={stack.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <Card className="h-full border-[#E5E7EB] hover:shadow-lg transition-shadow">
                      <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <Boxes className="w-5 h-5 text-[#991B1B]" />
                          <span className="text-xs font-medium text-[#1E3A8A]/60 capitalize">{stack.targetLab} Lab</span>
                        </div>
                        <h4 className="font-heading font-bold text-[#1E3A8A] mb-2">{stack.name}</h4>
                        <p className="text-sm text-[#1E3A8A]/70 mb-3">{stack.purpose}</p>
                        <div className="flex flex-wrap gap-1 mb-3">
                          {getStackTools(stack.id)?.slice(0, 3).map((tool, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 rounded bg-[#F8FAFC] text-[#1E3A8A]">
                              {tool?.name}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-[#1E3A8A]/50">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {stack.estimatedSetupTime}
                          </span>
                          <span>{stack.monthlyInvestment}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* Development Pathway */}
            <section data-testid="development-pathway">
              <h2 className="font-heading text-2xl font-bold mb-6" style={{ color: '#991B1B' }}>
                Your Development Pathway
              </h2>
              <Card className="border-[#E5E7EB]">
                <CardContent className="p-6">
                  <div className="space-y-6">
                    {developmentPathway.map((phase, index) => (
                      <motion.div
                        key={phase.step}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.15 }}
                        className="flex gap-4"
                      >
                        <div className="flex flex-col items-center">
                          <div className="w-10 h-10 rounded-full bg-[#991B1B] text-white flex items-center justify-center font-bold">
                            {phase.step}
                          </div>
                          {index < developmentPathway.length - 1 && (
                            <div className="w-0.5 h-full bg-[#E5E7EB] mt-2" />
                          )}
                        </div>
                        <div className="flex-1 pb-6">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-[#1E3A8A]/10 text-[#1E3A8A] text-xs">{phase.phase}</Badge>
                            <span className="text-xs text-[#1E3A8A]/50">{phase.estimatedDuration}</span>
                          </div>
                          <h4 className="font-heading font-bold text-[#1E3A8A] mb-2">{phase.title}</h4>
                          <p className="text-sm text-[#1E3A8A]/70 mb-3">{phase.description}</p>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {phase.focusAreas.map((area, i) => (
                              <span key={i} className="text-xs px-2 py-1 rounded bg-[#F8FAFC] text-[#1E3A8A]">
                                {area}
                              </span>
                            ))}
                          </div>
                          <div className="space-y-1">
                            {phase.milestones.map((milestone, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs text-[#1E3A8A]/60">
                                <CheckCircle className="w-3 h-3 text-green-500" />
                                {milestone}
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </section>
          </div>
        ) : (
          /* No Assessment State */
          <div className="text-center py-16">
            <Brain className="w-16 h-16 text-[#1E3A8A]/30 mx-auto mb-6" />
            <h2 className="font-heading text-2xl font-bold text-[#1E3A8A] mb-4">
              Complete Your Assessment
            </h2>
            <p className="text-[#1E3A8A]/70 mb-8 max-w-md mx-auto">
              Take the Workforce Intelligence Assessment to unlock your personalized profile, 
              matched opportunities, and development pathway.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/observatories/global/survey">
                <Button size="lg" className="bg-[#991B1B] text-white hover:bg-[#7F1D1D]">
                  Start Assessment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => handleLoadDemo('developing')}
                className="border-[#1E3A8A] text-[#1E3A8A]"
              >
                Try Demo Profile
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
